import { useEffect } from "react";
import { useCompany } from "../company/companyStates";
import { editCompany } from "../company/editCompany";
import { useEditState } from "../expenses/expenseStates";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { getCompany } from "@/api/company/get-Company";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Controller } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { states } from "../utils/states";
import { Button } from "../ui/button";

const EditCompanyOnInvoice = () => {
  const { isEditOpen, setIsEditOpen } = useEditState();

  const { control, handleSubmit, register, updateCompanyFn } = editCompany();

  const { company, setCompany } = useCompany();

  useEffect(() => {
    (async () => {
      try {
        const companyRes = await getCompany();
        setCompany(companyRes.company[0]); // Assuming company is an array in the response
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    })();
  }, []);

  const onSubmitCompany = handleSubmit(async (data) => {
    if (company) {
      try {
        await updateCompanyFn({
          companyId: company?.id || "",
          companyAddress: data.companyAddress,
          companyCity: data.companyCity,
          companyState: data.companyState,
          companyZip: data.companyZip,
          companyName: data.companyName,
          companyEmail: data.companyEmail,
        });
        setIsEditOpen(false);
      } catch (err) {
        console.error(err);
        toast.error("Error updating company");
      }
    }
  });

  return (
    <div className="flex flex-col space-y-1 font-Hemmers text-black">
      <p className="text-xl text-black">Invoice</p>
      <p>{company?.companyName}</p>
      <p>{company?.companyAddress}</p>
      <p>{`${company?.companyCity}, ${company?.companyState || ""} ${company?.companyZip || ""}`}</p>
      <p>{company?.companyEmail}</p>
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogTrigger asChild>
          <span className="cursor-pointer font-Hemmers text-sm text-blue-500">
            Edit Company
          </span>
        </DialogTrigger>
        <DialogContent className="border-transparent bg-inherit">
          <DialogHeader>
            <DialogTitle className="text-black">Edit Company</DialogTitle>
            <DialogDescription>Fill in to edit company</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmitCompany}>
            <div className="w-full">
              <label
                htmlFor="companyName"
                className="block text-sm font-medium text-black"
              >
                Company Name
              </label>
              <Input
                {...register("companyName", {
                  required: "Company name is required",
                })}
                type="text"
                id="companyName"
                autoComplete="off"
                className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
              />

              <div className="mt-4">
                <label
                  htmlFor="companyEmail"
                  className="block text-sm font-medium text-black"
                >
                  Company Email
                </label>
                <Input
                  {...register("companyEmail", {
                    required: "Company email is required",
                  })}
                  type="text"
                  id="companyEmail"
                  autoComplete="off"
                  className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                />
              </div>
              <div className="mt-4">
                <label
                  htmlFor="companyAddress"
                  className="block text-sm font-medium text-black"
                >
                  Company Address
                </label>
                <Input
                  {...register("companyAddress", {
                    required: "Company address is required",
                  })}
                  type="text"
                  id="companyAddress"
                  autoComplete="off"
                  className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                />
              </div>

              <div className="flex space-x-4">
                <div className="mt-4">
                  <label
                    htmlFor="companyState"
                    className="block text-sm font-medium text-black"
                  >
                    State
                  </label>
                  <Controller
                    name="companyState"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger
                          className={`mt-1 w-[80px] rounded-md border-zinc-300 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm ${
                            field.value ? "text-zinc-900" : "text-gray-500"
                          }`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="w-[80px]">
                          {states.map((state) => (
                            <SelectItem key={state.value} value={state.label}>
                              {state.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="mt-4">
                  <label
                    htmlFor="companyCity"
                    className="block text-sm font-medium text-black"
                  >
                    Company City
                  </label>
                  <Input
                    type="text"
                    id="companyCity"
                    autoComplete="off"
                    className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    placeholder="City"
                    {...register("companyCity")}
                  />
                </div>
                <div className="mt-4">
                  <label
                    htmlFor="companyZip"
                    className="block text-sm font-medium text-black"
                  >
                    Company Zip
                  </label>
                  <Input
                    type="text"
                    id="companyZip"
                    autoComplete="off"
                    className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    placeholder="Zip"
                    {...register("companyZip")}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Button
                className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                type="submit"
                onClick={() => {
                  setIsEditOpen(false);
                }}
              >
                Edit Company
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditCompanyOnInvoice;
